import React, { useState, useEffect, useRef } from 'react';
import { ArrowDown } from 'lucide-react';
import { format, isBefore } from 'date-fns';
import { DatePickerButton } from './DatePickerButton';

export interface DateRangeValuesSectionProps {
  selectedValues: string[];
  onRangeChange: (startDate: string, endDate: string) => void;
  validationErrors?: string[];
  onValidationError?: (hasError: boolean) => void;
}

/**
 * DateRangeValuesSection - Date range picker for date-based filters
 * Used for DATE column types with BETWEEN operator
 * Values are stored as two ISO date-time strings (YYYY-MM-DDTHH:mm:ss)
 * selectedValues[0] = start date-time, selectedValues[1] = end date-time
 */
export const DateRangeValuesSection: React.FC<DateRangeValuesSectionProps> = ({
  selectedValues,
  onRangeChange,
  validationErrors = [],
  onValidationError,
}) => {
  const [fromError, toError] = validationErrors;
  const [fromDate, setFromDate] = useState<Date | undefined>(
    selectedValues[0] ? new Date(selectedValues[0]) : undefined
  );
  const [toDate, setToDate] = useState<Date | undefined>(
    selectedValues[1] ? new Date(selectedValues[1]) : undefined
  );
  const [rangeError, setRangeError] = useState<string | undefined>(undefined);

  // Each picker popover is parent-controlled so a day-click can close it and
  // hand focus to the sibling trigger.
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);
  const fromTriggerRef = useRef<HTMLButtonElement>(null);
  const toTriggerRef = useRef<HTMLButtonElement>(null);

  // Notify parent of validation error state changes
  useEffect(() => {
    onValidationError?.(!!rangeError);
  }, [rangeError, onValidationError]);

  // Reset the pickers when the draft is cleared externally (avoids stale dates).
  const [prevSelectedValues, setPrevSelectedValues] = useState(selectedValues);
  if (selectedValues !== prevSelectedValues) {
    setPrevSelectedValues(selectedValues);
    if (selectedValues.length === 0) {
      setFromDate(undefined);
      setToDate(undefined);
      setRangeError(undefined);
    }
  }

  const handleFromDateSelect = (date: Date | undefined): void => {
    if (!date) return;
    const newDate = new Date(date);
    setFromDate(newDate);

    const startDate = format(newDate, "yyyy-MM-dd'T'HH:mm:ss");
    const hasInvalidDateRange =
      toDate !== undefined && isBefore(toDate, newDate);

    if (hasInvalidDateRange) {
      // Inverted range: keep the From popover open, surface the error, no commit.
      setRangeError('End date must be after start date');
      return;
    }
    setRangeError(undefined);

    // Stage into the draft with '' for the missing To so Apply validates the
    // half the user can see; the filter still commits only via Apply.
    onRangeChange(
      startDate,
      toDate ? format(toDate, "yyyy-MM-dd'T'HH:mm:ss") : ''
    );
    setFromOpen(false);
  };

  const handleToDateSelect = (date: Date | undefined): void => {
    if (!date) return;
    const newDate = new Date(date);
    setToDate(newDate);

    const hasInvalidDateRange =
      fromDate !== undefined && isBefore(newDate, fromDate);
    const endDate = format(newDate, "yyyy-MM-dd'T'HH:mm:ss");

    if (hasInvalidDateRange) {
      // Inverted range: keep the To popover open, surface the error, no apply.
      setRangeError('End date must be after start date');
      return;
    }
    setRangeError(undefined);

    // Stage into the draft with '' for the missing From so Apply validates the
    // half the user can see; the filter still commits only via Apply.
    onRangeChange(
      fromDate ? format(fromDate, "yyyy-MM-dd'T'HH:mm:ss") : '',
      endDate
    );
    setToOpen(false);
  };

  // Time edits and day-clicks only stage the draft; Apply commits the range.
  const handleFromTimeChange = (date: Date): void => {
    const newDate = new Date(date);
    setFromDate(newDate);
    if (toDate && isBefore(toDate, newDate)) {
      setRangeError('End date must be after start date');
      return;
    }
    setRangeError(undefined);
    onRangeChange(
      format(newDate, "yyyy-MM-dd'T'HH:mm:ss"),
      toDate ? format(toDate, "yyyy-MM-dd'T'HH:mm:ss") : ''
    );
  };

  const handleToTimeChange = (date: Date): void => {
    const newDate = new Date(date);
    setToDate(newDate);
    if (fromDate && isBefore(newDate, fromDate)) {
      setRangeError('End date must be after start date');
      return;
    }
    setRangeError(undefined);
    onRangeChange(
      fromDate ? format(fromDate, "yyyy-MM-dd'T'HH:mm:ss") : '',
      format(newDate, "yyyy-MM-dd'T'HH:mm:ss")
    );
  };

  return (
    <div className="flex flex-col gap-3 p-4">
      {/* From Date Picker */}
      <div className="flex flex-col gap-2">
        <label className="text-xs font-medium text-muted-foreground">
          From
        </label>
        <DatePickerButton
          selectedDate={fromDate}
          onDateSelect={handleFromDateSelect}
          onTimeChange={handleFromTimeChange}
          placeholder="Select start date"
          showTime
          rangeStart={fromDate}
          rangeEnd={toDate}
          open={fromOpen}
          onOpenChange={setFromOpen}
          triggerRef={fromTriggerRef}
          focusOnCloseRef={toTriggerRef}
        />
        {fromError && (
          <div className="text-xs text-destructive">{fromError}</div>
        )}
      </div>

      {/* Arrow Indicator */}
      <div className="flex items-center justify-center">
        <ArrowDown className="h-4 w-4 text-muted-foreground" />
      </div>

      {/* To Date Picker */}
      <div className="flex flex-col gap-2">
        <label className="text-xs font-medium text-muted-foreground">To</label>
        <DatePickerButton
          selectedDate={toDate}
          onDateSelect={handleToDateSelect}
          onTimeChange={handleToTimeChange}
          placeholder="Select end date"
          showTime
          rangeStart={fromDate}
          rangeEnd={toDate}
          open={toOpen}
          onOpenChange={setToOpen}
          triggerRef={toTriggerRef}
          focusOnCloseRef={fromTriggerRef}
        />
        {toError && <div className="text-xs text-destructive">{toError}</div>}
        {rangeError && (
          <div className="text-xs text-destructive">{rangeError}</div>
        )}
      </div>
    </div>
  );
};
